/**
 * User-facing errors that survive translation.
 *
 * The problem: ~1.100 `ConvexError("…")` call sites carry German text. In an
 * English interface a German toast is exactly the half-finished result this
 * migration exists to prevent — but rewriting all of them at once would mean a
 * window where errors surface as blank or as a raw key.
 *
 * So the payload is **additive**. `userError()` keeps the German message
 * exactly where it is today and attaches a message key beside it:
 *
 *     throw userError("Vorname und Nachname sind erforderlich.", {
 *       key: "errors.users.nameRequired",
 *     });
 *
 * The frontend prefers `i18n.key` when present and falls back to `message`
 * otherwise, so a call site that has not been converted yet behaves exactly as
 * it does today. Nothing breaks mid-migration, and progress is measurable:
 * the number of `ConvexError` sites without an `i18n` payload only goes down.
 *
 * Deliberately a pure leaf in `logic/` — it imports only `convex/values`, so the
 * Next.js app can import the type without dragging the backend module graph in.
 */

import { ConvexError } from "convex/values";

export type UserErrorI18n = {
  /**
   * Message key, resolved against `apps/app/messages/<locale>/`.
   * Fully qualified, including the namespace: `"errors.invoices.locked"`.
   */
  readonly key: string;
  /**
   * ICU placeholder values. Restricted to what next-intl accepts, so a value
   * that cannot be rendered is rejected here rather than at the user's screen.
   */
  readonly params?: Readonly<Record<string, string | number>>;
};

export type UserErrorData = {
  /**
   * German text. Still the source of truth for any client that does not know
   * about `i18n`, and the fallback while a call site is unconverted.
   */
  readonly message: string;
  readonly i18n?: UserErrorI18n;
};

/**
 * Builds a `ConvexError` whose payload the UI can translate.
 *
 * The German message stays mandatory on purpose: it documents the error at the
 * call site, it keeps `git grep` useful, and it is what shows if the key is
 * ever missing from a catalog.
 */
export function userError(
  message: string,
  i18n?: UserErrorI18n,
): ConvexError<UserErrorData> {
  return new ConvexError<UserErrorData>(
    i18n === undefined ? { message } : { message, i18n },
  );
}

/** Narrows unknown error data to a payload carrying a translatable key. */
export function readUserErrorI18n(data: unknown): UserErrorI18n | null {
  if (data === null || typeof data !== "object") return null;
  const candidate = (data as { i18n?: unknown }).i18n;
  if (candidate === null || typeof candidate !== "object") return null;

  const key = (candidate as { key?: unknown }).key;
  if (typeof key !== "string" || key.trim().length === 0) return null;

  const params = (candidate as { params?: unknown }).params;
  if (params === undefined) return { key };
  if (params === null || typeof params !== "object") return { key };

  const clean: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(params)) {
    if (typeof value === "string" || typeof value === "number") {
      clean[name] = value;
    }
  }
  return { key, params: clean };
}

/**
 * Der deutsche Text eines Backend-Fehlers, egal in welcher Form er ankommt.
 *
 * `userError()` hat die Nutzlast von einem blossen String auf
 * `{ message, i18n }` erweitert. Jede Stelle im Frontend, die vorher
 * `typeof data === "string"` prueft oder `String(data)` bildet, verliert damit
 * die Meldung — sie zeigt danach ihren allgemeinen Ersatztext oder woertlich
 * `[object Object]`. Beides ist schlimmer als der deutsche Satz, den sie vorher
 * hatte. Diese Funktion liest beide Formen, damit es dafuer genau einen Ort
 * gibt statt vier.
 *
 * Fuer Text in der Sprache des Nutzers ist `useServerErrorMessage()` zustaendig;
 * hier geht es nur darum, den vorhandenen Satz nicht zu verlieren.
 */
export function readUserErrorMessage(data: unknown): string | null {
  if (typeof data === "string") {
    return data.trim().length > 0 ? data : null;
  }
  if (data === null || typeof data !== "object" || !("message" in data)) {
    return null;
  }
  const message = (data as { message?: unknown }).message;
  return typeof message === "string" && message.trim().length > 0
    ? message
    : null;
}

/**
 * Reads {@link readUserErrorMessage} from a thrown value's `.data`.
 * Convex wraps `userError` so `error.message` is JSON — never show that.
 */
export function readThrownUserErrorMessage(error: unknown): string | null {
  if (error === null || typeof error !== "object" || !("data" in error)) {
    return null;
  }
  return readUserErrorMessage((error as { data: unknown }).data);
}
